// Budget + ROI cards extracted from `pages/ViewReportPage.js` (Phase 2a split).
// Pure presentation — owns no state, reads everything from the report prop.
import React from "react";
import { Card, CardContent } from "@/components/ui/card";

const _fmt$ = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

export function BudgetTile({ label, value, color, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4" data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1.5" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export function BudgetTotals({ report, primary }) {
  const items = report.budget_items || [];
  const budgetTotal = Number(report.budget_total || 0);
  const actualTotal = Number(report.actual_total || 0);
  const variance = budgetTotal - actualTotal;
  const variancePct = budgetTotal > 0 ? (variance / budgetTotal) * 100 : null;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="view-report-budget">
      <CardContent className="p-6">
        <div className="mb-4 border-b border-zinc-100 pb-3">
          <div className="label-overline">Budget vs actual</div>
          <h2 className="font-display font-bold text-xl tracking-tight mt-1">
            Budget &amp; actual cost — this month
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <BudgetTile label="Total monthly budget" value={_fmt$(budgetTotal)}
            color="#09090B" testid="view-report-budget-total" />
          <BudgetTile label="Actual marketing cost" value={_fmt$(actualTotal)}
            color={primary} testid="view-report-actual-total" />
          <BudgetTile
            label={variance >= 0 ? "Under budget by" : "Over budget by"}
            value={`${_fmt$(Math.abs(variance))}${variancePct != null ? ` (${Math.abs(variancePct).toFixed(1)}%)` : ""}`}
            color={variance >= 0 ? "#16a34a" : "#dc2626"}
            testid="view-report-budget-variance" />
        </div>

        {items.length > 0 && (
          <>
            <div className="label-overline mb-2">Budget breakdown</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 pr-3">Item</th>
                  <th className="text-left py-2 px-3">Unit</th>
                  <th className="text-right py-2 px-3">Amount</th>
                  <th className="text-right py-2 px-3">Rate $/hr</th>
                  <th className="text-right py-2 pl-3">Monthly $</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const unit = (it.unit || "").toLowerCase();
                  const amt = Number(it.amount || 0);
                  const rate = Number(it.hourly_rate || 0);
                  let monthly = 0;
                  if (unit === "hrs") monthly = amt * rate;
                  else if (["$", "aud", "usd"].includes(unit)) monthly = amt;
                  return (
                    <tr key={it.item_id || it.label} className="border-b border-zinc-100">
                      <td className="py-2 pr-3 font-semibold">{it.label}</td>
                      <td className="py-2 px-3 text-zinc-600">{it.unit}</td>
                      <td className="py-2 px-3 text-right font-mono">{amt}</td>
                      <td className="py-2 px-3 text-right font-mono">{unit === "hrs" ? _fmt$(rate) : "—"}</td>
                      <td className="py-2 pl-3 text-right font-mono font-semibold">
                        {monthly > 0 ? _fmt$(monthly) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function RoiSummary({ report, primary }) {
  const actualCost = Number(report.actual_total || 0);
  const budgetTotal = Number(report.budget_total || 0);
  const totalCost = actualCost + budgetTotal;  // platform spend + agency retainer
  const conversions = Number(report.total_conversions || 0);
  const costPerConv = conversions > 0 ? totalCost / conversions : null;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="view-report-roi"
      style={{ pageBreakBefore: "always" }}>
      <CardContent className="p-8">
        <div className="border-b border-zinc-100 pb-4 mb-5">
          <div className="label-overline">Total costs &amp; ROI</div>
          <h2 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Investment summary
          </h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Combined platform spend + agency retainer for the month, with conversion
            efficiency. Revenue-based ROI will appear here once revenue tracking is
            wired in.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BudgetTile label="Total marketing investment" value={_fmt$(totalCost)}
            color={primary} testid="roi-total-cost" />
          <BudgetTile label="Total conversions"
            value={conversions > 0 ? conversions.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
            color="#09090B" testid="roi-conversions" />
          <BudgetTile label="Cost per conversion"
            value={costPerConv != null ? _fmt$(costPerConv) : "— pending"}
            color={costPerConv != null ? primary : "#A1A1AA"} testid="roi-cost-per-conv" />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="label-overline">Cost breakdown</div>
            <ul className="text-sm text-zinc-700 mt-2 space-y-1">
              <li className="flex justify-between">
                <span>Platform ad spend (tracked)</span>
                <span className="font-mono font-semibold">{_fmt$(actualCost)}</span>
              </li>
              <li className="flex justify-between">
                <span>Agency retainer (budget)</span>
                <span className="font-mono font-semibold">{_fmt$(budgetTotal)}</span>
              </li>
              <li className="flex justify-between border-t border-zinc-200 pt-2 mt-2">
                <strong>Total monthly investment</strong>
                <strong className="font-mono" style={{ color: primary }}>{_fmt$(totalCost)}</strong>
              </li>
            </ul>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
            <div className="label-overline">Return on investment</div>
            <p className="text-sm text-zinc-700 mt-2">
              {conversions > 0 ? (
                <>
                  This month delivered <strong>{conversions.toLocaleString()}</strong> tracked
                  conversion{conversions === 1 ? "" : "s"} at an average{" "}
                  <strong>{_fmt$(costPerConv)}</strong> each. Once revenue per conversion
                  is supplied (Brief &amp; Budget page) we&apos;ll automatically show ROI&nbsp;%.
                </>
              ) : (
                <>
                  No conversions tracked yet for this period — upload a Google Ads /
                  Meta Ads conversion report or wire a GA4 goal to populate this section
                  with cost-per-conversion and ROI.
                </>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
